import React, { useState, useEffect, memo, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Save, Activity, Heart, CheckCircle, Plus, X, AlertTriangle, Shield, Zap, Trash2, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import InjuryHealingDialog from '../InjuryHealingDialog';
import InjuryCaptureDialog from '../InjuryCaptureDialog';

const ZONE_ICONS = {
  epaule: '💪', bas_du_dos: '🔙', genou: '🦵', hanche: '🦴',
  poignet: '✋', cheville: '🦶', cou_cervicales: '🧣', coude: '💪',
};

const SEVERITY_COLORS = {
  mineur: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300',
  modere: 'border-orange-500/40 bg-orange-500/10 text-orange-300',
  majeur: 'border-red-500/40 bg-red-500/10 text-red-300',
};

const SettingsInjuryMood = memo(function SettingsInjuryMood() {
  const { user } = useAuth();
  const { language } = useLanguage();

  const [zones, setZones] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [activeInjuries, setActiveInjuries] = useState([]);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newZone, setNewZone] = useState('');
  const [newSeverity, setNewSeverity] = useState('mineur');
  const [newNotes, setNewNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Mood state
  const [moodScore, setMoodScore] = useState(null);
  const [moodSaving, setMoodSaving] = useState(false);
  // Phase 2 retroactive (2026-04-29) — null = today, else YYYY-MM-DD
  const [moodTargetDate, setMoodTargetDate] = useState(null);

  // 🩹 Injury-to-Workout pipeline dialog
  const [injuryDialogOpen, setInjuryDialogOpen] = useState(false);
  const [todayMoodLogged, setTodayMoodLogged] = useState(false);
  const [moodHistory, setMoodHistory] = useState([]);
  const [healingDialog, setHealingDialog] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [zonesRes, injuriesRes, moodRes] = await Promise.all([
        api.get('/injuries/zones'),
        api.get('/injuries'),
        api.get('/users/mood/history').catch(() => ({ data: {} })),
      ]);
      setZones(zonesRes.data.zones || []);
      setSeverities(zonesRes.data.severities || []);
      setActiveInjuries(injuriesRes.data.injuries || []);
      setRecoveryMode(injuriesRes.data.recovery_mode || false);
      setMoodHistory(moodRes.data.logs || []);
      if (moodRes.data.today) {
        setMoodScore(moodRes.data.today.score);
        setTodayMoodLogged(true);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addInjury = async () => {
    if (!newZone) return;
    setSaving(true);
    try {
      await api.post('/injuries', { zone: newZone, severity: newSeverity, notes: newNotes });
      toast.success(language === 'fr' ? 'Blessure enregistrée' : 'Injury saved');
      // Génération automatique du plan temporaire de réadaptation
      try {
        await api.post(`/injuries/${newZone}/temporary-plan`);
        toast.success(language === 'fr' ? 'Plan de réadaptation activé' : 'Rehab plan activated');
      } catch {
        // silent — failure of rehab plan generation must not block injury declaration
      }
      setAdding(false);
      setNewZone('');
      setNewSeverity('mineur');
      setNewNotes('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const healInjury = async (zone) => {
    try {
      const res = await api.post(`/injuries/${zone}/start-healing`);
      // Le dialogue Coach YUMIX s'ouvre — l'utilisateur choisit reprise graduelle ou nouveau plan
      setHealingDialog(res.data);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur de guérison' : 'Healing error'));
    }
  };

  const hardDeleteInjury = async (zone) => {
    if (!window.confirm(language === 'fr'
      ? 'Supprimer définitivement cette blessure ? Cette action est irréversible.'
      : 'Permanently delete this injury? This action is irreversible.')) {
      return;
    }
    try {
      await api.delete(`/injuries/${zone}/permanent`);
      toast.success(language === 'fr' ? 'Blessure supprimée' : 'Injury deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const updateSeverity = async (zone, severity) => {
    try {
      await api.put(`/injuries/${zone}`, { severity });
      toast.success(language === 'fr' ? 'Severite mise a jour' : 'Severity updated');
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const saveMood = async (score) => {
    setMoodSaving(true);
    setMoodScore(score);
    try {
      const payload = { score, note: '' };
      if (moodTargetDate) payload.target_date = moodTargetDate;
      await api.post('/users/mood', payload);
      // Only mark as "logged today" when the entry is actually for today
      if (!moodTargetDate) setTodayMoodLogged(true);
      toast.success(
        moodTargetDate
          ? (language === 'fr' ? `Humeur enregistrée pour le ${moodTargetDate}` : `Mood saved for ${moodTargetDate}`)
          : (language === 'fr' ? 'Humeur enregistrée' : 'Mood logged')
      );
      const res = await api.get('/users/mood/history');
      setMoodHistory(res.data.logs || []);
      // After a retroactive save, reset the picker to today so next click is daily by default
      setMoodTargetDate(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setMoodSaving(false);
    }
  };

  // Zones not yet injured
  const availableZones = zones.filter(z => !activeInjuries.find(i => i.zone === z.id));

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      {/* INJURY INTELLIGENCE */}
      <div className="mt-8">
        <Card className="bg-card border-border" data-testid="injury-intelligence-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
              <Shield className="w-5 h-5 text-orange-400" />
              {language === 'fr' ? 'Intelligence des Blessures' : 'Injury Intelligence'}
            </CardTitle>
            <CardDescription>
              {language === 'fr'
                ? 'Déclare tes zones blessées. L\'IA adaptera automatiquement tes séances en substituant les exercices dangereux par des alternatives sécuritaires.'
                : 'Declare your injured zones. AI will automatically adapt workouts by substituting dangerous exercises with safe alternatives.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* 🚨 NEW — Quick injury-to-workout pipeline trigger */}
            <Button
              size="lg"
              onClick={() => setInjuryDialogOpen(true)}
              className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:opacity-90 font-heading uppercase tracking-wide"
              data-testid="open-injury-capture-dialog"
            >
              <Zap className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Adapter mon entraînement IA' : 'Adapt my AI training'}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center -mt-2">
              {language === 'fr'
                ? "L'IA génère un plan adapté en 15 s · Bouclier médical automatique"
                : 'AI generates an adapted plan in 15s · Automatic medical shield'}
            </p>

            {/* Recovery Mode Alert */}
            {recoveryMode && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-red-500/10 border-2 border-red-500/40 rounded-xl flex items-start gap-3"
                data-testid="recovery-mode-alert"
              >
                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-heading uppercase text-red-300 tracking-tight">
                    {language === 'fr' ? 'Mode Récupération Actif' : 'Recovery Mode Active'}
                  </p>
                  <p className="text-xs text-red-300/70 mt-1">
                    {language === 'fr'
                      ? 'Blessure majeure détectée. Seuls les exercices de mobilité et nutrition seront proposés.'
                      : 'Major injury detected. Only mobility and nutrition exercises will be suggested.'}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Active Injuries List */}
            {activeInjuries.length > 0 && (
              <div className="space-y-2" data-testid="active-injuries-list">
                <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground">
                  {language === 'fr' ? 'Zones Blessées Actives' : 'Active Injured Zones'} ({activeInjuries.length})
                </p>
                <AnimatePresence>
                  {activeInjuries.map(injury => {
                    const zoneDef = zones.find(z => z.id === injury.zone);
                    const zoneLabel = zoneDef ? (language === 'fr' ? zoneDef.label_fr : zoneDef.label_en) : injury.zone;
                    const icon = ZONE_ICONS[injury.zone] || '🩹';
                    return (
                      <motion.div
                        key={injury.zone}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className={`p-3 rounded-xl border-2 ${SEVERITY_COLORS[injury.severity] || SEVERITY_COLORS.mineur}`}
                        data-testid={`injury-${injury.zone}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{icon}</span>
                            <div>
                              <p className="text-sm font-bold">{zoneLabel}</p>
                              {injury.notes && <p className="text-[10px] opacity-70 mt-0.5">{injury.notes}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Severity selector */}
                            <div className="flex gap-1">
                              {['mineur', 'modere', 'majeur'].map(sev => (
                                <button
                                  key={sev}
                                  onClick={() => updateSeverity(injury.zone, sev)}
                                  className={`px-2 py-1 rounded text-[9px] font-bold uppercase transition-all ${
                                    injury.severity === sev
                                      ? sev === 'mineur' ? 'bg-yellow-500/30 text-yellow-300'
                                        : sev === 'modere' ? 'bg-orange-500/30 text-orange-300'
                                        : 'bg-red-500/30 text-red-300'
                                      : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'
                                  }`}
                                  data-testid={`severity-${injury.zone}-${sev}`}
                                >
                                  {sev === 'mineur' ? (language === 'fr' ? 'Min' : 'Min')
                                    : sev === 'modere' ? (language === 'fr' ? 'Mod' : 'Mod')
                                    : (language === 'fr' ? 'Maj' : 'Maj')}
                                </button>
                              ))}
                            </div>
                            {/* Heal button — opens auto Coach YUMIX dialog */}
                            <button
                              onClick={() => healInjury(injury.zone)}
                              className="p-1.5 rounded-lg hover:bg-emerald-500/20 transition-colors"
                              title={language === 'fr' ? 'Marquer comme guérie' : 'Mark as healed'}
                              data-testid={`heal-${injury.zone}`}
                            >
                              <CheckCircle className="w-4 h-4 text-emerald-400" />
                            </button>
                            {/* Hard delete — supprime sans dialogue Coach YUMIX */}
                            <button
                              onClick={() => hardDeleteInjury(injury.zone)}
                              className="p-1.5 rounded-lg hover:bg-red-500/20 transition-colors"
                              title={language === 'fr' ? 'Supprimer définitivement' : 'Delete permanently'}
                              data-testid={`delete-${injury.zone}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}

            {/* Add Injury Form */}
            {adding ? (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-3 p-4 border border-orange-500/30 rounded-xl bg-orange-500/5"
              >
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                    {language === 'fr' ? 'Zone Blessée' : 'Injured Zone'}
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="zone-selector">
                    {availableZones.map(zone => (
                      <button
                        key={zone.id}
                        onClick={() => setNewZone(zone.id)}
                        className={`p-2.5 rounded-lg text-xs font-medium transition-all border flex items-center gap-2 ${
                          newZone === zone.id
                            ? 'bg-orange-500/20 border-orange-500/50 text-orange-300'
                            : 'bg-secondary border-border text-muted-foreground hover:border-orange-500/30'
                        }`}
                        data-testid={`zone-btn-${zone.id}`}
                      >
                        <span>{ZONE_ICONS[zone.id] || '🩹'}</span>
                        {language === 'fr' ? zone.label_fr : zone.label_en}
                      </button>
                    ))}
                  </div>
                </div>

                {newZone && (
                  <>
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                        {language === 'fr' ? 'Sévérité' : 'Severity'}
                      </Label>
                      <div className="grid grid-cols-3 gap-2" data-testid="severity-selector">
                        {severities.map(sev => (
                          <button
                            key={sev.id}
                            onClick={() => setNewSeverity(sev.id)}
                            className={`p-3 rounded-lg text-xs transition-all border ${
                              newSeverity === sev.id
                                ? SEVERITY_COLORS[sev.id] + ' border-2'
                                : 'bg-secondary border-border text-muted-foreground hover:border-orange-500/30'
                            }`}
                            data-testid={`new-severity-${sev.id}`}
                          >
                            <p className="font-bold text-[10px] uppercase mb-1">
                              {language === 'fr' ? sev.label_fr : sev.label_en}
                            </p>
                            <p className="text-[9px] opacity-70">{sev.description_fr}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">
                        {language === 'fr' ? 'Notes (optionnel)' : 'Notes (optional)'}
                      </Label>
                      <Input
                        value={newNotes}
                        onChange={(e) => setNewNotes(e.target.value)}
                        placeholder={language === 'fr' ? 'Ex: Entorse cheville droite...' : 'Ex: Right ankle sprain...'}
                        className="bg-secondary border-input text-sm"
                        data-testid="injury-notes-input"
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={addInjury} disabled={saving} className="flex-1" data-testid="save-injury-btn">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        {language === 'fr' ? 'Enregistrer' : 'Save'}
                      </Button>
                      <Button variant="outline" onClick={() => { setAdding(false); setNewZone(''); }} data-testid="cancel-injury-btn">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                )}
              </motion.div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setAdding(true)}
                className="w-full border-dashed border-orange-500/30 text-orange-300 hover:bg-orange-500/10"
                data-testid="add-injury-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Déclarer une blessure' : 'Report an injury'}
              </Button>
            )}

            {/* Info Box */}
            <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
              <p className="text-xs text-primary/80 flex items-center gap-2 mb-1">
                <Zap className="w-3.5 h-3.5 shrink-0" />
                <span className="font-bold uppercase">{language === 'fr' ? 'Substitution Automatique' : 'Auto Substitution'}</span>
              </p>
              <p className="text-[10px] text-muted-foreground">
                {language === 'fr'
                  ? 'Le générateur IA filtrera automatiquement les exercices dangereux pour tes zones blessées et les remplacera par des alternatives sécuritaires.'
                  : 'The AI generator will automatically filter dangerous exercises for your injured zones and replace them with safe alternatives.'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* MOOD CHECK */}
      <div className="mt-8">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
              <Heart className="w-5 h-5 text-purple-400" />
              {language === 'fr' ? 'Comment te sens-tu moralement ?' : 'How are you feeling mentally?'}
            </CardTitle>
            <CardDescription>
              {language === 'fr'
                ? 'Ton moral compte autant que ta nutrition. Si ça ne va pas, ton coach sera alerté pour te soutenir.'
                : 'Your mood matters as much as your nutrition. If things aren\'t going well, your coach will be alerted to support you.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Phase 2 retroactive — discreet date row. Today (default) = subtle.
                Past day = orange "Rattrapage". Allows logging mood for a missed
                day even if today already has an entry. */}
            {(() => {
              const today = new Date();
              const ymdLocal = (d) => {
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${y}-${m}-${day}`;
              };
              const todayStr = ymdLocal(today);
              const minDate = (() => { const d = new Date(); d.setDate(d.getDate() - 30); return ymdLocal(d); })();
              const isRetro = !!moodTargetDate;
              return (
                <div
                  className={`px-2.5 py-1.5 rounded-lg border transition-all ${
                    isRetro ? 'bg-orange-500/10 border-orange-500/40' : 'bg-secondary/40 border-border/50'
                  }`}
                  data-testid="mood-date-row"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <CalendarDays className={`w-3.5 h-3.5 shrink-0 ${isRetro ? 'text-orange-400' : 'text-muted-foreground'}`} />
                      <span className={`text-[11px] uppercase tracking-widest font-bold truncate ${
                        isRetro ? 'text-orange-300' : 'text-muted-foreground'
                      }`}>
                        {isRetro
                          ? (language === 'fr' ? `Rattrapage · ${moodTargetDate}` : `Backfill · ${moodTargetDate}`)
                          : (language === 'fr' ? "Aujourd'hui" : 'Today')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="date"
                        value={moodTargetDate || todayStr}
                        min={minDate}
                        max={todayStr}
                        onChange={(e) => {
                          const v = e.target.value || todayStr;
                          setMoodTargetDate(v === todayStr ? null : v);
                        }}
                        className="h-6 text-[11px] bg-transparent border-0 p-0 m-0 outline-none cursor-pointer text-muted-foreground hover:text-foreground"
                        data-testid="mood-date-input"
                      />
                      {isRetro && (
                        <button
                          type="button"
                          onClick={() => setMoodTargetDate(null)}
                          className="text-[10px] uppercase tracking-widest font-bold text-orange-400 hover:text-orange-300"
                        >
                          {language === 'fr' ? 'Aujourd.' : 'Today'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
            <div className="flex items-center justify-center gap-3" data-testid="mood-scale">
              {[
                { score: 1, label: language === 'fr' ? 'Très bas' : 'Very low', color: 'text-red-400 border-red-500/40 bg-red-500/10' },
                { score: 2, label: language === 'fr' ? 'Bas' : 'Low', color: 'text-orange-400 border-orange-500/40 bg-orange-500/10' },
                { score: 3, label: language === 'fr' ? 'Neutre' : 'Neutral', color: 'text-yellow-400 border-yellow-500/40 bg-yellow-500/10' },
                { score: 4, label: language === 'fr' ? 'Bien' : 'Good', color: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' },
                { score: 5, label: language === 'fr' ? 'Excellent' : 'Excellent', color: 'text-green-400 border-green-500/40 bg-green-500/10' },
              ].map(({ score, label, color }) => {
                // Allow click when in retroactive mode even if today's mood is already logged
                const disabled = moodSaving || (todayMoodLogged && !moodTargetDate);
                return (
                  <button key={score} onClick={() => !disabled && saveMood(score)} disabled={disabled}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all min-w-[56px] ${
                      moodScore === score ? color + ' ring-2 ring-offset-2 ring-offset-card' : disabled ? 'border-border/30 bg-secondary/30 opacity-50' : 'border-border bg-secondary hover:border-primary/40'
                    }`} data-testid={`mood-btn-${score}`}>
                    <span className="text-2xl font-heading font-black">{score}</span>
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</span>
                  </button>
                );
              })}
            </div>
            {todayMoodLogged && (
              <p className="text-center text-xs text-emerald-400">
                <CheckCircle className="w-3.5 h-3.5 inline mr-1" />
                {language === 'fr' ? `Humeur enregistrée aujourd'hui: ${moodScore}/5` : `Mood logged today: ${moodScore}/5`}
              </p>
            )}
            {moodHistory.length > 0 && (
              <div className="flex items-center justify-center gap-1 pt-2">
                <span className="text-[10px] text-muted-foreground mr-2">{language === 'fr' ? '7 derniers jours:' : 'Last 7 days:'}</span>
                {moodHistory.slice(0, 7).reverse().map((m, i) => (
                  <div key={i} className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold ${m.score <= 2 ? 'bg-red-500/20 text-red-400' : m.score === 3 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-emerald-500/20 text-emerald-400'}`} title={m.date}>
                    {m.score}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Coach YUMIX Auto-Healing Dialog */}
      <InjuryHealingDialog
        open={!!healingDialog}
        onClose={() => setHealingDialog(null)}
        payload={healingDialog}
        onResolved={() => fetchData()}
      />

      {/* 🩹 Injury-to-Workout Pipeline Dialog */}
      <InjuryCaptureDialog
        open={injuryDialogOpen}
        onOpenChange={setInjuryDialogOpen}
        onPlanGenerated={() => fetchData()}
      />
    </>
  );
});

export default SettingsInjuryMood;
